import {
    Card,
    CardContent,
    Typography,
    Stack,
    Divider,
    Button,
    Chip,
    Box,
    LinearProgress,
    Tooltip,
    IconButton,
    Avatar,
    Paper,
} from '@mui/material';
import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery, gql } from '@apollo/client';
import {
    Business as BusinessIcon,
    People as PeopleIcon,
    QrCodeScanner as QrCodeIcon,
    Visibility as VisibilityIcon,
    TrendingUp as TrendingUpIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

const GET_SCAN_DATA_BY_STAND_ID = gql`
    query GetScanDataByStandId($eventId: String!, $standId: String!) {
        ScanDataByStandId(eventId: $eventId, standId: $standId)
    }
`;

export default function LeadGenCard({ stand, type, totalScans, onEdit, onSendEmail }) {
    const navigate = useNavigate();
    const { eventId } = useParams();

    const standId = stand?.id;

    const { loading, error, data } = useQuery(GET_SCAN_DATA_BY_STAND_ID, {
        variables: { eventId, standId },
        skip: !standId || type !== 'admin',
    });

    const ScannedData = (data && data?.ScanDataByStandId && JSON.parse(data?.ScanDataByStandId)) || [];
    // Use totalScans from props (calculated in LeadGen.js) or from query, or from stand data
    const scansCount =
        totalScans !== undefined
            ? totalScans
            : stand?.totalScans !== undefined
            ? stand.totalScans
            : ScannedData?.length || 0;
    const staffCount = stand?.staff?.length || 0;

    // Calculate scan rate per staff (if staff exists)
    const scanRate = staffCount > 0 ? (scansCount / staffCount).toFixed(1) : 0;

    // Generate unique gradient color based on company name
    const getGradientColor = name => {
        if (!name) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
        const hash = name.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);
        const colors = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        ];
        return colors[Math.abs(hash) % colors.length];
    };

    const gradientColor = getGradientColor(stand?.company || stand?.name);

    return (
        <Card
            sx={{
                height: '100%',
                minHeight: '320px',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                position: 'relative',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    borderColor: 'primary.main',
                },
            }}
        >
            {/* Header with Gradient */}
            <Box
                sx={{
                    background: gradientColor,
                    p: 2.5,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Stack direction='column' justifyContent='space-between' alignItems='flex-start' spacing={2}>
                    <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
                        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 1 }}>
                            <Avatar
                                sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'rgba(255,255,255,0.25)',
                                    backdropFilter: 'blur(10px)',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                }}
                            >
                                <BusinessIcon />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant='h6'
                                    fontWeight={700}
                                    noWrap
                                    sx={{
                                        textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                        mb: 0.5,
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {type === 'admin' ? stand?.company || 'N/A' : stand?.name || 'N/A'}
                                </Typography>
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <Typography
                                        variant='caption'
                                        sx={{
                                            opacity: 0.95,
                                            fontWeight: 500,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        {type === 'admin' ? 'Exhibitor Stand' : 'Staff Member'}
                                    </Typography>
                                    {stand?.stallNo && (
                                        <>
                                            <Stack direction='row' spacing={0.5} alignItems='center'>
                                                <LocationIcon sx={{ fontSize: '0.75rem' }} />
                                                <Typography
                                                    variant='caption'
                                                    sx={{
                                                        opacity: 0.95,
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                    }}
                                                >
                                                    Stall {stand.stallNo}
                                                </Typography>
                                            </Stack>
                                        </>
                                    )}
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                    <Stack direction='row' spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
                        {onEdit && (
                            <Tooltip title='Edit Account' arrow>
                                <IconButton
                                    size='small'
                                    sx={{
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.25)',
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s',
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onEdit();
                                    }}
                                >
                                    <EditIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        )}
                        {onSendEmail && (
                            <Tooltip title='Send Email' arrow>
                                <IconButton
                                    size='small'
                                    sx={{
                                        color: 'white',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.25)',
                                            transform: 'scale(1.1)',
                                        },
                                        transition: 'all 0.2s',
                                    }}
                                    onClick={e => {
                                        e.stopPropagation();
                                        onSendEmail();
                                    }}
                                >
                                    <EmailIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title='View Details' arrow>
                            <IconButton
                                size='small'
                                sx={{
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s',
                                }}
                                onClick={() => navigate(`${stand.id}`, { state: { stand } })}
                            >
                                <VisibilityIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Box>

            <CardContent
                sx={{
                    flexGrow: 1,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    pb: 2,
                }}
            >
                {type === 'admin' ? (
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        {/* Metrics Grid - More Compact */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 1.25,
                                mb: 1.5,
                            }}
                        >
                            {/* Staff Count */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.25,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(102, 126, 234, 0.08)',
                                    border: '1px solid',
                                    borderColor: 'rgba(102, 126, 234, 0.2)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: 'rgba(102, 126, 234, 0.12)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: 1,
                                    },
                                }}
                            >
                                <Stack spacing={0.5}>
                                    <Stack direction='row' spacing={0.75} alignItems='center'>
                                        <Box
                                            sx={{
                                                p: 0.5,
                                                borderRadius: 1,
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                            }}
                                        >
                                            <PeopleIcon sx={{ fontSize: '0.85rem' }} />
                                        </Box>
                                        <Typography
                                            variant='caption'
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                fontSize: '0.65rem',
                                            }}
                                        >
                                            Staff
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        variant='h6'
                                        sx={{
                                            fontWeight: 700,
                                            color: 'primary.main',
                                            lineHeight: 1.2,
                                            fontSize: '1.15rem',
                                        }}
                                    >
                                        {staffCount}
                                    </Typography>
                                </Stack>
                            </Paper>

                            {/* Total Scans */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.25,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(76, 175, 80, 0.08)',
                                    border: '1px solid',
                                    borderColor: 'rgba(76, 175, 80, 0.2)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: 'rgba(76, 175, 80, 0.12)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: 1,
                                    },
                                }}
                            >
                                <Stack spacing={0.5}>
                                    <Stack direction='row' spacing={0.75} alignItems='center'>
                                        <Box
                                            sx={{
                                                p: 0.5,
                                                borderRadius: 1,
                                                bgcolor: 'success.main',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                            }}
                                        >
                                            <QrCodeIcon sx={{ fontSize: '0.85rem' }} />
                                        </Box>
                                        <Typography
                                            variant='caption'
                                            sx={{
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                fontSize: '0.65rem',
                                            }}
                                        >
                                            Scans
                                        </Typography>
                                    </Stack>
                                    <Typography
                                        variant='h6'
                                        sx={{
                                            fontWeight: 700,
                                            color: 'success.main',
                                            lineHeight: 1.2,
                                            fontSize: '1.15rem',
                                        }}
                                    >
                                        {loading ? (
                                            <LinearProgress size={16} sx={{ width: '50%', borderRadius: 1 }} />
                                        ) : (
                                            scansCount
                                        )}
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Box>

                        {/* Scan Rate - Full Width */}
                        {staffCount > 0 && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.25,
                                    borderRadius: 1.5,
                                    bgcolor: 'rgba(255, 152, 0, 0.08)',
                                    border: '1px solid',
                                    borderColor: 'rgba(255, 152, 0, 0.2)',
                                    transition: 'all 0.2s',
                                    mb: 1.5,
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 152, 0, 0.12)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: 1,
                                    },
                                }}
                            >
                                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Box
                                            sx={{
                                                p: 0.5,
                                                borderRadius: 1,
                                                bgcolor: 'warning.main',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 24,
                                                height: 24,
                                            }}
                                        >
                                            <TrendingUpIcon sx={{ fontSize: '0.85rem' }} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    fontSize: '0.65rem',
                                                    display: 'block',
                                                }}
                                            >
                                                Avg/Staff
                                            </Typography>
                                            <Typography
                                                variant='body1'
                                                sx={{
                                                    fontWeight: 700,
                                                    color: 'warning.main',
                                                    mt: 0.25,
                                                    fontSize: '0.95rem',
                                                }}
                                            >
                                                {scanRate}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Paper>
                        )}

                        {loading && (
                            <Box sx={{ mb: 1 }}>
                                <LinearProgress size='small' sx={{ borderRadius: 1 }} />
                            </Box>
                        )}

                        {error && (
                            <Chip
                                label='Error loading data'
                                color='error'
                                size='small'
                                sx={{ mb: 1, alignSelf: 'flex-start' }}
                            />
                        )}
                    </Box>
                ) : (
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.5,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(102, 126, 234, 0.08)',
                                border: '1px solid',
                                borderColor: 'rgba(102, 126, 234, 0.2)',
                                mb: 1.5,
                            }}
                        >
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <Box
                                    sx={{
                                        p: 0.5,
                                        borderRadius: 1,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 24,
                                        height: 24,
                                    }}
                                >
                                    <PeopleIcon sx={{ fontSize: '0.85rem' }} />
                                </Box>
                                <Typography variant='body2' fontWeight={600} color='text.secondary' fontSize='0.875rem'>
                                    Staff Member
                                </Typography>
                            </Stack>
                        </Paper>
                        <Box sx={{ mt: 'auto', pt: 1.5 }}>
                            <Button
                                variant='contained'
                                color='primary'
                                fullWidth
                                endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem' }} />}
                                onClick={() => navigate(`${stand.id}`, { state: { stand } })}
                                sx={{
                                    borderRadius: 1.5,
                                    py: 1,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                View Details
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Action Button - Always Visible at Bottom */}
                <Box sx={{ mt: 'auto', pt: 1.5 }}>
                    <Button
                        variant='contained'
                        color='primary'
                        fullWidth
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem' }} />}
                        onClick={() => navigate(`${stand.id}`, { state: { stand } })}
                        sx={{
                            borderRadius: 1.5,
                            py: 1,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                            '&:hover': {
                                boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                transform: 'translateY(-1px)',
                            },
                        }}
                    >
                        View Details
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}
